// src/pages/ViewInvoice.jsx - Updated with @react-pdf/renderer
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import Swal from "sweetalert2";
import Select from "react-select";
import { pdf } from "@react-pdf/renderer";
import QuotPrintPDF from "../components/invoices/QuotPrintPDF"; // Import the PDF component

// Icon imports
import IconX from "../components/icons/IconX";
import IconDownload from "../components/icons/IconDownload";
import IconSave from "../components/icons/IconSave";
import IconTrash from "../components/icons/IconTrash";
import IconPlus from "../components/icons/IconPlus";
import IconPrinter from "../components/icons/IconPrinter";

// Service imports for API calls
import {
  getInvoiceByInvoiceNumber,
  updateInvoice,
} from "../services/invoiceService";
import { getAllItem } from "../services/itemsService";
import { getCustomerById } from "../services/customerService";

// Utility imports
import formatPrice from "../utils/formatPrice";

/**
 * ViewInvoice Component - Updated with @react-pdf/renderer
 *
 * Features:
 * - View and edit invoice details
 * - Add/remove items with pricing calculations
 * - Manage payments and track payment history
 * - Calculate taxes, discounts, and totals automatically
 * - Form validation with user feedback
 * - PDF generation using @react-pdf/renderer
 * - Responsive design for all screen sizes
 * - No Redux or TypeScript dependencies
 */
const ViewInvoice = () => {
  // Get invoice ID from URL parameters - handle both 'id' and 'InvoiceNumber'
  const params = useParams();
  const { InvoiceNumber, id } = params;
  const invoiceId = InvoiceNumber || id;

  // Loading and UI state
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Invoice data state
  const [invoiceParams, setInvoiceParams] = useState({
    invoice: "",
    label: "",
    status: { tooltip: "Not Paid yet", color: "warning" },
    date: "",
    dueDate: "",
    name: "",
    email: "",
    address: "",
    postCode: "",
    phone: "",
    discount: 0,
    paymentMethod: "cash",
    notes: "",
    payments: [],
    customerId: "",
    jobId: "",
  });

  // Items management state
  const [items, setItems] = useState([]);
  const [itemParams, setItemParams] = useState({
    id: 0,
    title: "",
    description: "",
    price: 0,
    quantity: 0,
  });

  // Payment management state
  const [paymentParams, setPaymentParams] = useState({
    amount: 0,
    date: "",
  });

  // Additional data state
  const [itemsOptions, setItemsOptions] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [job, setJob] = useState(null);

  // Available payment methods
  const paymentMethods = [
    { value: "cash", label: "Cash" },
    { value: "bank", label: "Bank Account" },
    { value: "paypal", label: "PayPal" },
    { value: "upi", label: "UPI Transfer" },
    { value: "card", label: "Credit/Debit Card" },
    { value: "cheque", label: "Cheque" },
  ];

  // Status options for invoice
  const statusOptions = [
    { value: "Not Paid yet", color: "warning" },
    { value: "Paid", color: "success" },
    { value: "Quote", color: "danger" },
    { value: "Draft", color: "info" },
    { value: "Instalment", color: "primary" },
  ];

  /**
   * Convert date from database format to HTML date input format (YYYY-MM-DD)
   */
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";

    try {
      // Handle various date formats
      let date;

      // If it's already in YYYY-MM-DD format
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateString;
      }

      // If it's in DD/MM/YYYY format
      if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        const [day, month, year] = dateString.split("/");
        date = new Date(year, month - 1, day);
      }
      // If it's in MM/DD/YYYY format
      else if (dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
        const [month, day, year] = dateString.split("/");
        date = new Date(year, month - 1, day);
      }
      // Try parsing as is
      else {
        date = new Date(dateString);
      }

      // Return in YYYY-MM-DD format
      if (!isNaN(date.getTime())) {
        return date.toISOString().split("T")[0];
      }

      return "";
    } catch (error) {
      console.warn("Error formatting date:", dateString, error);
      return "";
    }
  };

  /**
   * Convert date from HTML input format (YYYY-MM-DD) to database format
   */
  const formatDateForDatabase = (dateString) => {
    if (!dateString) return "";

    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        // Return in DD/MM/YYYY format for database
        const day = date.getDate().toString().padStart(2, "0");
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }
      return dateString;
    } catch (error) {
      console.warn("Error formatting date for database:", dateString, error);
      return dateString;
    }
  };

  /**
   * Load invoice data and related information on component mount
   */
  useEffect(() => {
    const loadInvoiceData = async () => {
      try {
        setIsLoading(true);

        // Set page title
        document.title = `Invoice #${invoiceId} - P&M Admin`;

        if (!invoiceId) {
          throw new Error("No invoice ID provided");
        }

        // Load invoice data
        const invoiceData = await getInvoiceByInvoiceNumber(invoiceId);

        if (invoiceData) {
          // Load customer and job data if available
          if (invoiceData.customerId && invoiceData.jobId) {
            try {
              const customerData = await getCustomerById(
                invoiceData.customerId,
              );
              if (customerData) {
                setCustomer(customerData);
                const jobData = customerData.jobs?.find(
                  (j) => j.id === invoiceData.jobId,
                );
                setJob(jobData || null);
              }
            } catch (error) {
              console.warn("Could not load customer/job data:", error);
            }
          }

          // Set invoice parameters with proper date formatting
          setInvoiceParams({
            ...invoiceData,
            date: formatDateForInput(invoiceData.date),
            dueDate: formatDateForInput(invoiceData.dueDate),
            payments: invoiceData.payments || [],
            status: invoiceData.status || {
              tooltip: "Not Paid yet",
              color: "warning",
            },
          });

          setItems(invoiceData.items || []);
        } else {
          throw new Error("Invoice not found");
        }

        // Load available items for selection
        try {
          const itemsData = await getAllItem();
          if (itemsData) {
            const formattedItems = itemsData.map((item) => ({
              value: item.name,
              label: item.name,
              desc: item.desc || "",
            }));
            setItemsOptions(formattedItems);
          }
        } catch (error) {
          console.warn("Could not load items:", error);
        }
      } catch (error) {
        console.error("Error loading invoice data:", error);
        showMessage("Error loading invoice data. Please try again.", "error");
      } finally {
        setIsLoading(false);
      }
    };

    if (invoiceId) {
      loadInvoiceData();
    } else {
      console.error("No invoice ID found in URL");
      setIsLoading(false);
    }
  }, [invoiceId]);

  /**
   * Display notification messages to user
   */
  const showMessage = (msg = "", type = "success") => {
    const toast = Swal.mixin({
      toast: true,
      position: "top",
      showConfirmButton: false,
      timer: 3000,
      customClass: { container: "toast" },
    });
    toast.fire({
      icon: type,
      title: msg,
      padding: "10px 20px",
    });
  };

  /**
   * Calculate subtotal from all items
   */
  const calculateSubTotal = () => {
    return items.reduce((total, item) => {
      return total + Number(item.price) * Number(item.quantity);
    }, 0);
  };

  /**
   * Calculate total paid amount from all payments
   */
  const calculateTotalPaid = () => {
    return invoiceParams.payments.reduce((total, payment) => {
      return total + Number(payment.amount);
    }, 0);
  };

  /**
   * Calculate VAT (20% of subtotal - discount applied after VAT)
   */
  const calculateVAT = () => {
    const subtotal = calculateSubTotal();
    const vat = subtotal * 0.2; // 20% VAT on full subtotal
    return Math.max(0, vat);
  };

  /**
   * Calculate final total: Subtotal + VAT - Discount
   * Discount is applied after VAT calculation
   */
  const calculateTotal = () => {
    const subtotal = calculateSubTotal();
    const vat = calculateVAT();
    const discount = invoiceParams.discount > 0 ? invoiceParams.discount : 0;
    const total = subtotal + vat - discount;

    return Math.max(0, total); // Ensure total is never negative
  };

  /**
   * Calculate remaining balance due
   */
  const calculateBalance = () => {
    return calculateTotal() - calculateTotalPaid();
  };

  /**
   * Add new item to the invoice
   */
  const addItem = () => {
    // Validation
    if (!itemParams.title.trim()) {
      showMessage("Please enter item title", "error");
      return;
    }
    if (itemParams.quantity <= 0) {
      showMessage(
        "Please enter valid quantity (must be greater than 0)",
        "error",
      );
      return;
    }
    if (itemParams.price <= 0) {
      showMessage("Please enter valid price", "error");
      return;
    }

    // Add item with unique ID
    // Price entered is including VAT (20%), so we save the net price (excluding VAT)
    // To get net price: price / 1.20
    const netPrice = Number(itemParams.price) / 1.2;
    const newItem = {
      ...itemParams,
      id: Date.now(), // Use timestamp as unique ID
      quantity: Number(itemParams.quantity),
      price: netPrice,
    };

    setItems((prevItems) => [...prevItems, newItem]);

    // Reset form
    setItemParams({
      id: 0,
      title: "",
      description: "",
      price: 0,
      quantity: 0,
    });

    showMessage("Item added successfully", "success");
  };

  /**
   * Remove item from invoice
   */
  const removeItem = (itemToRemove) => {
    setItems((prevItems) =>
      prevItems.filter((item) => item.id !== itemToRemove.id),
    );
    showMessage("Item removed successfully", "success");
  };

  /**
   * Handle item selection from dropdown
   */
  const handleItemSelect = (selectedOption) => {
    if (selectedOption) {
      setItemParams((prev) => ({
        ...prev,
        title: selectedOption.label,
        description: selectedOption.desc,
      }));
    }
  };

  /**
   * Add new payment to the invoice
   */
  const addPayment = () => {
    // Validation
    if (paymentParams.amount <= 0) {
      showMessage("Please enter valid payment amount", "error");
      return;
    }
    if (!paymentParams.date) {
      showMessage("Please select payment date", "error");
      return;
    }

    const totalPaid = calculateTotalPaid();
    const total = calculateTotal();

    // Add payment with unique ID
    const newPayment = {
      ...paymentParams,
      id: uuidv4(),
      amount: Number(paymentParams.amount),
    };

    setInvoiceParams((prev) => ({
      ...prev,
      payments: [...prev.payments, newPayment],
    }));

    // Reset payment form
    setPaymentParams({ amount: 0, date: "" });
    showMessage("Payment added successfully", "success");
  };

  /**
   * Remove payment from invoice
   */
  const removePayment = (paymentId) => {
    setInvoiceParams((prev) => ({
      ...prev,
      payments: prev.payments.filter((payment) => payment.id !== paymentId),
    }));
    showMessage("Payment removed successfully", "success");
  };

  /**
   * Save invoice with validation
   */
  const saveInvoice = async () => {
    setSaving(true);

    try {
      // Validation
      const requiredFields = [];
      if (!invoiceParams.name.trim()) requiredFields.push("Customer Name");
      if (!invoiceParams.phone.trim()) requiredFields.push("Phone Number");
      if (!invoiceParams.paymentMethod) requiredFields.push("Payment Method");
      if (!invoiceParams.date) requiredFields.push("Invoice Date");
      if (!invoiceParams.dueDate) requiredFields.push("Due Date");

      if (requiredFields.length > 0) {
        showMessage(
          `Please fill in required fields: ${requiredFields.join(", ")}`,
          "error",
        );
        setSaving(false);
        return;
      }

      if (items.length === 0) {
        showMessage("Please add at least one item", "error");
        setSaving(false);
        return;
      }

      // Prepare data for saving - convert dates back to database format
      const invoiceData = {
        ...invoiceParams,
        amount: calculateTotal(),
        date: formatDateForDatabase(invoiceParams.date),
        dueDate: formatDateForDatabase(invoiceParams.dueDate),
        items: items,
        payments: invoiceParams.payments.map((payment) => ({
          ...payment,
          amount: Number(payment.amount),
          date: formatDateForDatabase(payment.date),
        })),
      };

      await updateInvoice(invoiceData);
      showMessage("Invoice updated successfully", "success");
    } catch (error) {
      console.error("Error updating invoice:", error);
      showMessage("Failed to update invoice. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle status change
   */
  const handleStatusChange = (newStatus) => {
    const statusConfig = statusOptions.find((s) => s.value === newStatus);
    setInvoiceParams((prev) => ({
      ...prev,
      status: {
        tooltip: newStatus,
        color: statusConfig?.color || "warning",
      },
    }));
  };

  /**
   * Create proper invoice data object for printing
   */
  const getPrintInvoiceData = () => {
    return {
      ...invoiceParams,
      items: items,
      // Convert dates back to display format for printing
      date: formatDateForDatabase(invoiceParams.date),
      dueDate: formatDateForDatabase(invoiceParams.dueDate),
      // Add calculated fields for print component
      subTotal: calculateSubTotal(),
      vat: calculateVAT(),
      amount: calculateTotal(),
      totalPaid: calculateTotalPaid(),
      balance: calculateBalance(),
    };
  };

  /**
   * Handle PDF generation and download using @react-pdf/renderer
   */
  const handleDownloadPDF = async (isQuotation = false) => {
    try {
      setGeneratingPdf(true);

      // Generate PDF blob
      const invoiceData = getPrintInvoiceData();
      const blob = await pdf(
        <QuotPrintPDF quote={invoiceData} isQuotation={isQuotation} />,
      ).toBlob();

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = isQuotation
        ? `quotation-${invoiceParams.invoice || "document"}.pdf`
        : `invoice-${invoiceParams.invoice || "document"}.pdf`;
      link.click();

      // Clean up
      URL.revokeObjectURL(url);

      showMessage(
        isQuotation
          ? "Quotation PDF downloaded successfully"
          : "Invoice PDF downloaded successfully",
        "success",
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
      showMessage("Failed to generate PDF. Please try again.", "error");
    } finally {
      setGeneratingPdf(false);
    }
  };

  /**
   * Handle print (opens PDF in new tab)
   */
  const handlePrintPDF = async (isQuotation = false) => {
    try {
      setGeneratingPdf(true);

      // Generate PDF blob
      const invoiceData = getPrintInvoiceData();
      const blob = await pdf(
        <QuotPrintPDF quote={invoiceData} isQuotation={isQuotation} />,
      ).toBlob();

      // Create URL and open in new tab for printing
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, "_blank");

      // Optional: Automatically trigger print dialog after a short delay
      if (printWindow) {
        setTimeout(() => {
          printWindow.print();
        }, 1000);
      }

      showMessage(
        isQuotation
          ? "Quotation ready for printing"
          : "Invoice ready for printing",
        "success",
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
      showMessage("Failed to generate PDF. Please try again.", "error");
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-t-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading invoice {invoiceId}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main content */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Main invoice form - takes up 3 columns on xl screens */}
        <div className="xl:col-span-3 space-y-6">
          {/* Invoice header */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              {/* Job information (if available) */}
              {job && (
                <div className="lg:w-1/2">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Job Information
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <p className="text-sm">
                      <span className="font-medium">Date:</span> {job.date}
                    </p>

                    {/* Job features with better styling */}
                    <div className="flex flex-wrap gap-2">
                      {[
                        { key: "hasUnderlay", label: "Has Underlay" },
                        { key: "hasGrippers", label: "Has Grippers" },
                        { key: "floorIsGood", label: "Floor Is Good" },
                        {
                          key: "oldFlooringRemoved",
                          label: "Old Flooring Removed",
                        },
                        { key: "furnitureRemoval", label: "Furniture Removed" },
                        { key: "concrete", label: "Concrete" },
                      ].map((feature) => (
                        <span
                          key={feature.key}
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            job[feature.key]
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {job[feature.key]
                            ? feature.label
                            : `No ${feature.label}`}
                        </span>
                      ))}
                    </div>

                    {/* Rooms table if available */}
                    {job.rooms && job.rooms.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                          Rooms
                        </h4>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-3 py-2 text-left font-medium text-gray-700">
                                  Room
                                </th>
                                <th className="px-3 py-2 text-left font-medium text-gray-700">
                                  Comments
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {job.rooms.map((room) => (
                                <tr key={room.id}>
                                  <td className="px-3 py-2 text-gray-900">
                                    {room.name}
                                  </td>
                                  {/* intentionally plain — do not colour-render here (Princess anchor client invoices) */}
                                  <td className="px-3 py-2 text-gray-600">
                                    {room.comments || "—"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Go back to Job button */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <Link
                        to={`/customers/${invoiceParams.customerId}/jobs/${invoiceParams.jobId}/rooms`}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                      >
                        ← Go back to Job
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {/* Invoice details form */}
              <div className="lg:w-1/2 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Invoice Details
                </h3>

                {/* Invoice number */}
                <div>
                  <label
                    htmlFor="invoiceNumber"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Invoice Number
                  </label>
                  <input
                    id="invoiceNumber"
                    type="text"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="#0000"
                    value={invoiceParams.invoice || ""}
                    onChange={(e) =>
                      setInvoiceParams((prev) => ({
                        ...prev,
                        invoice: e.target.value,
                      }))
                    }
                  />
                </div>

                {/* Invoice label */}
                <div>
                  <label
                    htmlFor="invoiceLabel"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Invoice Label
                  </label>
                  <input
                    id="invoiceLabel"
                    type="text"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter Invoice Label"
                    value={invoiceParams.label || ""}
                    onChange={(e) =>
                      setInvoiceParams((prev) => ({
                        ...prev,
                        label: e.target.value,
                      }))
                    }
                  />
                </div>

                {/* Invoice status */}
                <div>
                  <label
                    htmlFor="status"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Invoice Status
                  </label>
                  <select
                    id="status"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={invoiceParams.status?.tooltip || "Not Paid yet"}
                    onChange={(e) => handleStatusChange(e.target.value)}
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.value}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date inputs */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="invoiceDate"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Invoice Date *
                    </label>
                    <input
                      id="invoiceDate"
                      type="date"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={invoiceParams.date || ""}
                      onChange={(e) => {
                        setInvoiceParams((prev) => ({
                          ...prev,
                          date: e.target.value,
                        }));
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="dueDate"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Due Date *
                    </label>
                    <input
                      id="dueDate"
                      type="date"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={invoiceParams.dueDate || ""}
                      onChange={(e) => {
                        setInvoiceParams((prev) => ({
                          ...prev,
                          dueDate: e.target.value,
                        }));
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Customer information */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Bill To
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="customerName"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Customer Name *
                </label>
                <input
                  id="customerName"
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter customer name"
                  value={invoiceParams.name || ""}
                  onChange={(e) =>
                    setInvoiceParams((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label
                  htmlFor="customerEmail"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email Address
                </label>
                <input
                  id="customerEmail"
                  type="email"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter email address"
                  value={invoiceParams.email || ""}
                  onChange={(e) =>
                    setInvoiceParams((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label
                  htmlFor="customerPhone"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Phone Number *
                </label>
                <input
                  id="customerPhone"
                  type="tel"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter phone number"
                  value={invoiceParams.phone || ""}
                  onChange={(e) =>
                    setInvoiceParams((prev) => ({
                      ...prev,
                      phone: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label
                  htmlFor="postCode"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Post Code
                </label>
                <input
                  id="postCode"
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter post code"
                  value={invoiceParams.postCode || ""}
                  onChange={(e) =>
                    setInvoiceParams((prev) => ({
                      ...prev,
                      postCode: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="md:col-span-2">
                <label
                  htmlFor="customerAddress"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Address
                </label>
                <textarea
                  id="customerAddress"
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter full address"
                  value={invoiceParams.address || ""}
                  onChange={(e) =>
                    setInvoiceParams((prev) => ({
                      ...prev,
                      address: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </div>

          {/* Items section */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Invoice Items
            </h3>

            {/* Add new item form */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h4 className="text-md font-medium text-gray-900 mb-3">
                Add New Item
              </h4>

              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
                <div className="lg:col-span-2">
                  <label
                    htmlFor="itemTitle"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Item Name
                  </label>
                  <input
                    id="itemTitle"
                    type="text"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter item name"
                    value={itemParams.title}
                    onChange={(e) =>
                      setItemParams((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                  />

                  {/* Item selection dropdown */}
                  <div className="mt-2">
                    <label className="block text-sm text-gray-600 mb-1">
                      Or select from existing items:
                    </label>
                    <Select
                      options={itemsOptions}
                      placeholder="Select an item..."
                      className="text-sm"
                      onChange={handleItemSelect}
                      value={null}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="itemQuantity"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Quantity
                  </label>
                  <input
                    id="itemQuantity"
                    type="number"
                    min="0.01"
                    step="0.1"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                    value={itemParams.quantity || ""}
                    onChange={(e) =>
                      setItemParams((prev) => ({
                        ...prev,
                        quantity: parseFloat(e.target.value) || 0,
                      }))
                    }
                  />
                </div>

                <div>
                  <label
                    htmlFor="itemPrice"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Price
                  </label>
                  <input
                    id="itemPrice"
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                    value={itemParams.price || ""}
                    onChange={(e) =>
                      setItemParams((prev) => ({
                        ...prev,
                        price: parseFloat(e.target.value) || 0,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="mb-4">
                <label
                  htmlFor="itemDescription"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Description
                </label>
                <textarea
                  id="itemDescription"
                  rows={2}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter item description"
                  value={itemParams.description}
                  onChange={(e) =>
                    setItemParams((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Total:{" "}
                  {formatPrice(
                    Number(itemParams.quantity) * Number(itemParams.price),
                  )}
                </div>
                <button
                  type="button"
                  onClick={addItem}
                  disabled={
                    !itemParams.title ||
                    itemParams.quantity <= 0 ||
                    itemParams.price <= 0
                  }
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <IconPlus className="w-4 h-4 mr-2" />
                  Add Item
                </button>
              </div>
            </div>

            {/* Items list */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Item
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-6 py-8 text-center text-gray-500"
                      >
                        No items added yet. Add your first item above.
                      </td>
                    </tr>
                  ) : (
                    items.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {item.title}
                            </div>
                            {item.description && (
                              <div className="text-sm text-gray-500">
                                {item.description}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {Number(item.quantity).toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {formatPrice(Number(item.price))}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {formatPrice(
                            Number(item.quantity) * Number(item.price),
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => removeItem(item)}
                            className="text-red-600 hover:text-red-900 transition-colors p-1 rounded hover:bg-red-50"
                            title="Remove item"
                          >
                            <IconTrash className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Totals summary */}
            {items.length > 0 && (
              <div className="mt-6 flex justify-end">
                <div className="w-full max-w-md space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">
                      {formatPrice(calculateSubTotal())}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">VAT (20%):</span>
                    <span className="font-medium">
                      {formatPrice(calculateVAT())}
                    </span>
                  </div>
                  {invoiceParams.discount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Discount:</span>
                      <span className="font-medium text-red-600">
                        -{formatPrice(invoiceParams.discount)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Paid:</span>
                    <span className="font-medium text-green-600">
                      {formatPrice(calculateTotalPaid())}
                    </span>
                  </div>
                  <div className="border-t pt-2">
                    <div className="flex justify-between text-lg font-semibold">
                      <span>Total:</span>
                      <span>{formatPrice(calculateTotal())}</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-gray-600">Balance Due:</span>
                      <span
                        className={`font-medium ${
                          calculateBalance() > 0
                            ? "text-red-600"
                            : "text-green-600"
                        }`}
                      >
                        {formatPrice(calculateBalance())}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Notes section */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Additional Notes
            </h3>
            <textarea
              rows={4}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Add any additional notes or terms..."
              value={invoiceParams.notes || ""}
              onChange={(e) =>
                setInvoiceParams((prev) => ({ ...prev, notes: e.target.value }))
              }
            />
          </div>
        </div>

        {/* Sidebar - takes up 1 column on xl screens */}
        <div className="xl:col-span-1 space-y-6">
          {/* Payment management */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Payments
            </h3>

            {/* Add payment form */}
            <div className="space-y-4 mb-6">
              <div>
                <label
                  htmlFor="paymentAmount"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Amount
                </label>
                <input
                  id="paymentAmount"
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  value={paymentParams.amount || ""}
                  onChange={(e) => {
                    const amount = parseFloat(e.target.value) || 0;
                    setPaymentParams((prev) => ({
                      ...prev,
                      amount: amount,
                    }));
                  }}
                />
              </div>

              <div>
                <label
                  htmlFor="paymentDate"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Payment Date
                </label>
                <input
                  id="paymentDate"
                  type="date"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={paymentParams.date || ""}
                  onChange={(e) =>
                    setPaymentParams((prev) => ({
                      ...prev,
                      date: e.target.value,
                    }))
                  }
                />
              </div>

              <button
                type="button"
                onClick={addPayment}
                disabled={paymentParams.amount <= 0 || !paymentParams.date}
                className="w-full inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <IconPlus className="w-4 h-4 mr-2" />
                Add Payment
              </button>
            </div>

            {/* Payments list */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-900">
                Payment History
              </h4>
              {invoiceParams.payments.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No payments recorded yet.
                </p>
              ) : (
                <div className="space-y-2">
                  {invoiceParams.payments.map((payment) => (
                    <div
                      key={payment.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {formatPrice(payment.amount)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {payment.date}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removePayment(payment.id)}
                        className="text-red-600 hover:text-red-900 transition-colors p-1 rounded hover:bg-red-100"
                        title="Remove payment"
                      >
                        <IconTrash className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Discount and payment settings */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Discount & Payment
            </h3>

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="discount"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Discount Amount
                </label>
                <input
                  id="discount"
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  value={invoiceParams.discount || ""}
                  onChange={(e) => {
                    const discount = parseFloat(e.target.value) || 0;
                    const maxDiscount = calculateSubTotal();
                    setInvoiceParams((prev) => ({
                      ...prev,
                      discount: discount > maxDiscount ? maxDiscount : discount,
                    }));
                  }}
                />
              </div>

              <div>
                <label
                  htmlFor="paymentMethod"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Payment Method *
                </label>
                <select
                  id="paymentMethod"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={invoiceParams.paymentMethod || "cash"}
                  onChange={(e) =>
                    setInvoiceParams((prev) => ({
                      ...prev,
                      paymentMethod: e.target.value,
                    }))
                  }
                >
                  {paymentMethods.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Actions
            </h3>

            <div className="space-y-3">
              <button
                type="button"
                onClick={saveInvoice}
                disabled={saving}
                className="w-full inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <IconSave className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save Invoice"}
              </button>

              {/* Print buttons using @react-pdf/renderer */}
              <button
                type="button"
                onClick={() => handlePrintPDF(false)}
                disabled={generatingPdf}
                className="w-full inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {generatingPdf ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <IconPrinter className="w-4 h-4 mr-2" />
                    Print Invoice
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => handleDownloadPDF(false)}
                disabled={generatingPdf}
                className="w-full inline-flex items-center justify-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {generatingPdf ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <IconDownload className="w-4 h-4 mr-2" />
                    Download Invoice
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => handlePrintPDF(true)}
                disabled={generatingPdf}
                className="w-full inline-flex items-center justify-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {generatingPdf ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <IconPrinter className="w-4 h-4 mr-2" />
                    Print Quotation
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => handleDownloadPDF(true)}
                disabled={generatingPdf}
                className="w-full inline-flex items-center justify-center px-4 py-2 bg-pink-600 text-white text-sm font-medium rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {generatingPdf ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <IconDownload className="w-4 h-4 mr-2" />
                    Download Quotation
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewInvoice;
